<template>
  <form
    id="user-form"
    @submit.prevent="saveUser"
    v-if="isPermittedAll([
      'UsersGetByID'
    ])">
    <v-container
      class="h-100 d-flex flex-column pt-0">
      <!-- Title -->
      <v-row
        class="flex-grow-0">
        <v-col class="py-0">
          <h3>
            <span class="mr-1" v-if="$store.getters.isActiveUserDirty">*</span>
            <span v-if="activeUser.UserID">Edit User #{{activeUser.UserID}}</span>
            <span v-else>Add User</span>
          </h3>
        </v-col>
      </v-row>
      <!-- / Title -->

      <!-- Name / Email -->
      <v-row
        class="flex-grow-0">
        <v-col :cols="isMobile ? 12 : 'default'">
          <v-text-field
            id="user-given-name"
            name="userGivenName"
            ref="userSerialNumber"
            v-model="activeUser.UserGivenName"
            label="Name:"
            :readonly="$store.getters.isLoadingActive"
            hide-details>
          </v-text-field>
        </v-col>
        <v-col :cols="isMobile ? 12 : 'default'">
          <v-text-field
            id="user-email"
            name="userEmail"
            ref="userEmail"
            v-model="activeUser.UserEmailAddress"
            label="Email:"
            :readonly="$store.getters.isLoadingActive"
            hide-details>
          </v-text-field>
        </v-col>
      </v-row>
      <!-- / Name / Email -->

      <!-- Password -->
      <v-row
        v-if="!activeUser.UserID"
        class="flex-grow-0">
        <v-col :cols="isMobile ? 12 : 6">
          <v-text-field
            id="user-password"
            name="userPassword"
            ref="userPassword"
            v-model="activeUser.Password"
            label="Password:"
            :readonly="$store.getters.isLoadingActive"
            hide-details>
          </v-text-field>
        </v-col>
      </v-row>
      <!-- / Password  -->

      <!-- Location / Phone -->
      <v-row
        class="flex-grow-0">
        <v-col :cols="isMobile ? 12 : 'default'">
          <v-text-field
            id="user-location"
            name="userLocation"
            ref="userLocation"
            v-model="activeUser.UserLocation"
            label="Location:"
            :readonly="$store.getters.isLoadingActive"
            hide-details>
          </v-text-field>
        </v-col>
        <v-col :cols="isMobile ? 12 : 'default'">
          <v-text-field
            id="user-phone"
            name="userPhone"
            ref="userPhone"
            v-model="activeUser.UserPhoneNumber"
            label="Phone:"
            :readonly="$store.getters.isLoadingActive"
            hide-details>
          </v-text-field>
        </v-col>
      </v-row>
      <!-- / Location / Phone -->

      <!-- Notes -->
      <v-row
        class="flex-grow-0">
        <v-col
          class="d-flex flex-column">
          <label
            for="user-notes">
            Notes:
          </label>
          <v-textarea
            id="user-notes"
            name="userNotes"
            ref="userNotes"
            class="pt-0 mt-0"
            rows="3"
            v-model="activeUser.UserNotes"
            :readonly="$store.getters.isLoadingActive"
            hide-details
            no-resize>
          </v-textarea>
        </v-col>
      </v-row>
      <!-- Notes -->

      <!-- Role / Permissions -->
      <v-row
        v-if="activeUser.UserID
          && isPermittedAll([
            'AssignedPermissionsUpdate'
          ])">
        <v-col :cols="isMobile ? 12 : 'default'">
          <Autocomplete
            id="user-permission-role"
            label="Role:"
            :items="$store.state.roles"
            v-model="role"
            item-value="RoleID"
            item-text="RoleName">
          </Autocomplete>
        </v-col>
        <v-col :cols="isMobile ? 12 : 'default'">
          <label>Permissions For Role:</label>
          <v-data-table
            id="permissions-form-table"
            class="form-table"
            :headers="permissionsFields"
            :items="availablePermissions"
            mobile-breakpoint="-1"
            item-key="PermissionCode"
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            :options="{
              sortBy: ['PermissionTitle'],
              rowsPerPage: -1,
            }">
            <template
              v-slot:item="row">
              <tr>
                <td class="action-column">
                  <v-checkbox
                    :input-value="hasPermission(row.item.PermissionCode)"
                    disabled
                    hide-details>
                  </v-checkbox>
                </td>
                <td>{{row.item.PermissionTitle}}</td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <!-- / Role / Permissions -->

      <!-- Form Buttons -->
      <v-row>
        <v-col class="d-flex justify-end">
          <LastModifiedBy
            class="order-first ml-5"
            :entity="activeUser"
          />
        </v-col>
      </v-row>
      <v-row
        class="flex-grow-0">
        <v-col class="d-flex align-center">
          <v-btn
            v-if="isPermittedToSave"
            type="submit"
            class="ml-auto"
            color="primary"
            :disabled="$store.getters.isLoadingActive
              || !$store.getters.isActiveUserDirty">
            Save
          </v-btn>

          <v-btn
            @click="goToUsersTable"
            type="button"
            color="primary"
            class="order-first"
            outlined>
            Back
          </v-btn>

          <v-btn
            v-if="activeUser.UserID
              && isPermittedAll([
                'UsersDelete'
              ])
              && !activeUser.UserIsDeleted"
            type="button"
            color="error"
            class="order-first ml-5"
            @click="hideUser"
            :disabled="$store.getters.isLoadingActive"
            outlined>
            Hide User
          </v-btn>

          <v-btn
            v-if="activeUser.UserID
              && isPermittedAll([
                'UsersRestore'
              ])
              && activeUser.UserIsDeleted"
            type="button"
            color="error"
            class="order-first ml-5"
            @click="restoreUser"
            :disabled="$store.getters.isLoadingActive"
            outlined>
            Unhide User
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Form Buttons -->
    </v-container>
  </form>
</template>

<script>
import _ from 'lodash';
import LastModifiedBy from './LastModifiedBy.vue';

export default {
  name: 'UserForm',
  components: {
    LastModifiedBy,
  },
  data() {
    return {
      permissionsFields: [
        {
          text: '',
          value: 'action',
          sortable: false,
          class: 'action-column',
        },
        {
          text: 'Permission',
          value: 'PermissionTitle',
        },
      ],
    };
  },
  computed: {
    isPermittedToSave() {
      return (!this.activeUser.UserID
          && this.isPermittedAll(['UsersCreate']))
        || (this.activeUser.UserID
          && this.isPermittedAll(['UsersUpdate']));
    },
    activeUser() {
      return this.$store.state.activeUser;
    },
    availablePermissions() {
      return this.$store.state.availablePermissions;
    },
    activeUserPermissionCodes() {
      return _.map(
        this.$store.state.activeUserPermissions,
        'PermissionCode',
      );
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    role: {
      get() {
        const role = _.find(
          this.$store.state.roles,
          (role) => {
            const activeUserPermissionCodes = this.activeUserPermissionCodes
              .slice()
              .sort();

            const permissionCodes = role.PermissionCodes
              .slice()
              .sort();

            return _.isEqual(permissionCodes, activeUserPermissionCodes);
          },
        );

        return role;
      },
      set(role) {
        if (role && role.PermissionCodes) {
          this.$store.state.activeUserPermissions = _.map(
            role.PermissionCodes,
            (pc) => _.find(
              this.availablePermissions,
              { PermissionCode: pc },
            ),
          );
        }
      },
    },
  },
  methods: {
    saveUser() {
      let userPromise = Promise.resolve();

      if (this.activeUser.UserID) {
        userPromise = this.$store.dispatch('updateUser');
      } else {
        userPromise = this.$store.dispatch('createUser');
      }

      userPromise
        .then(() => this.goToUsersTable());
    },
    hideUser() {
      const confirm = () => {
        this.$store.dispatch('deleteUser')
          .then(() => {
            this.goToUsersTable();
          });
      };

      this.showConfirm(
        'Are you sure you want to hide this user?',
        'Hide User',
      ).then(confirm, _.noop);
    },
    restoreUser() {
      const confirm = () => {
        this.$store.dispatch('restoreUser')
          .then(() => {
            this.goToUsersTable();
          });
      };

      this.showConfirm(
        'Are you sure you want to unhide this user?',
        'Hide User',
      ).then(confirm, _.noop);
    },
    hasPermission(permissionCode) {
      return this.activeUserPermissionCodes.includes(permissionCode);
    },
  },
  created() {
    if (this.$route.params.UserID) {
      this.$store.dispatch('getUser', this.$route);
    }

    this.$store.dispatch('getRoles');
    this.$store.dispatch('getAvailablePermissions');
  },
};
</script>

<style lang="scss">
#user-form {
  overflow-y: auto;

  #permissions-form-table {
    .action-column {
      * {
        margin: 0 !important;
        padding: 0 !important;
      }

      .v-input--selection-controls__input {
        margin: 0 auto !important;
      }
    }

    .v-data-table__wrapper {
      min-height: 300px;
      max-height: calc(100vh - 510px);
    }
  }
}
</style>
